import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MESSAGE_CHANNEL, MESSAGE_KIND, fmtDateTime } from "./leadUtils";

// One lead's notifications as a horizontal timeline, opened from its row in
// the lead list: what was sent (oldest → newest), a "Now" marker, then what
// comes next (soonest first). Each message is a small card; its colour says
// how it went, and hovering it shows the details. Skipped / cancelled ones
// are kept out of the way behind a count.
//
// data: { sent, upcoming, notSent } as GET /api/leads/:id/messages returns it
// (loaded by the list, so "Expand all" is one request).

const DELIVERY = { queued: "Queued", sent: "Sent", delivered: "Delivered", read: "Read", failed: "Not delivered" };

// What a card shows as its state, and the colour key for it.
function stateOf(m) {
  if (m.status === "failed") return { key: "failed", label: "Failed" };
  if (m.status === "sent") {
    if (m.deliveryStatus === "failed") return { key: "failed", label: "Not delivered" };
    if (m.deliveryStatus === "read") return { key: "read", label: "Read" };
    if (m.deliveryStatus === "delivered") return { key: "delivered", label: "Delivered" };
    return { key: "sent", label: DELIVERY[m.deliveryStatus] || "Sent" };
  }
  if (m.status === "paused") return { key: "paused", label: "Paused" };
  if (m.status === "sending") return { key: "scheduled", label: "Sending" };
  if (m.status === "scheduled") return { key: "scheduled", label: "Scheduled" };
  return { key: "muted", label: m.status === "cancelled" ? "Cancelled" : "Skipped" };
}

const shortTime = (d) => (d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");

const Card = ({ m }) => {
  const st = stateOf(m);
  const when = m.sentAt || m.dueAt;
  const reason = m.error || m.deliveryFailure || m.skipReason || "";
  const details = [
    `${m.title || "Message"} — ${MESSAGE_CHANNEL[m.channel] || m.channel}, ${MESSAGE_KIND[m.kind] || m.kind}`,
    `${st.label} · ${fmtDateTime(when)}`,
    m.templateName ? `Template: ${m.templateName}` : "",
    m.attachment?.fileName ? `File: ${m.attachment.fileName}` : "",
    reason,
  ].filter(Boolean).join("\n");
  return (
    <div className="ld-tl-card" data-s={st.key} title={details}>
      <div className="ld-tl-top">
        <span className={`ld-tl-ch is-${m.channel}`}>{m.channel === "email" ? "Email" : "WhatsApp"}</span>
        <span className="ld-tl-time">{shortTime(when)}</span>
      </div>
      <div className="ld-tl-title">{m.title || "Message"}</div>
      <div className="ld-tl-state"><i />{st.label}{reason && st.key === "failed" ? " — hover for why" : ""}</div>
    </div>
  );
};

// to: where "Open lead" goes (the list passes its filters along for Previous / Next).
const LeadMessagesPanel = ({ leadId, data, error, to }) => {
  const trackRef = useRef(null);
  const nowRef = useRef(null);
  const [showNotSent, setShowNotSent] = useState(false);

  // Open at "Now": the latest sent on the left, what comes next on the right.
  useLayoutEffect(() => {
    const track = trackRef.current, now = nowRef.current;
    if (track && now) track.scrollLeft = Math.max(0, now.offsetLeft - track.clientWidth * 0.45);
  }, [data]);

  if (error) return <div className="ld-field-error">{error}</div>;
  if (!data) return <div className="ld-tl-loading">Loading notifications…</div>;

  const sent = [...data.sent].reverse();          // oldest → newest, leading up to now
  const { upcoming, notSent } = data;
  const empty = !sent.length && !upcoming.length;

  return (
    <div className="ld-tl">
      {empty ? (
        <div className="ld-tl-empty">No notifications sent or scheduled yet.</div>
      ) : (
        <div className="ld-tl-track" ref={trackRef}>
          {sent.map((m) => <Card key={m._id} m={m} />)}
          <div className="ld-tl-now" ref={nowRef}><span>Now</span></div>
          {upcoming.length ? upcoming.map((m) => <Card key={m._id} m={m} />) : <div className="ld-tl-none">Nothing scheduled</div>}
        </div>
      )}
      {showNotSent && notSent.length > 0 && (
        <div className="ld-tl-track is-muted">{notSent.map((m) => <Card key={m._id} m={m} />)}</div>
      )}
      <div className="ld-tl-foot">
        <span className="ld-tl-counts">
          <b>{data.sent.length}</b> sent · <b>{upcoming.length}</b> to come
          {notSent.length > 0 && <> · <button type="button" className="ld-link" onClick={() => setShowNotSent((v) => !v)}>{notSent.length} not sent {showNotSent ? "▴" : "▾"}</button></>}
        </span>
        <span className="ld-tl-legend">
          <span data-s="read"><i />Read</span><span data-s="delivered"><i />Delivered</span><span data-s="sent"><i />Sent</span>
          <span data-s="scheduled"><i />Scheduled</span><span data-s="paused"><i />Paused</span><span data-s="failed"><i />Failed</span>
        </span>
        <Link className="ld-link" to={to || `/leads/${leadId}`}>Open lead →</Link>
      </div>
    </div>
  );
};

export default LeadMessagesPanel;
